import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Divider,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PaymentIcon from '@mui/icons-material/Payment';
import { useCart } from '../context/CartContext';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: 16,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
}));

const SummaryItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1.5),
}));

const Cart = () => {
  const navigate = useNavigate();
  const { 
    updateQuantity,
    removeFromCart,
    getCartItems,
    calculateSubtotal,
    calculateShipping,
    calculateTax,
    calculateTotal 
  } = useCart();
  
  const cartItems = getCartItems();
  
  // Go to shop page
  const goToShop = () => {
    navigate('/#shop-section');
  };
  
  // Handle quantity changes
  const handleQuantityChange = (productId, newValue) => {
    const quantity = parseInt(newValue, 10);
    if (!isNaN(quantity) && quantity >= 0) {
      updateQuantity(productId, quantity);
    }
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return '£' + amount.toFixed(2);
  };
  
  // If cart is empty, show empty cart view
  if (cartItems.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <IconButton onClick={goToShop} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" display="inline">
            Your Cart
          </Typography>
        </Box>
        
        <StyledPaper>
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <ShoppingCartIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>Your Cart is Empty</Typography>
            <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 500, mx: 'auto', mb: 4 }}>
              You haven't added any products to your cart yet. Browse our memorial QR code products to get started.
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={goToShop}
              startIcon={<ShoppingCartIcon />}
            >
              Shop QR Codes
            </Button>
          </Box>
        </StyledPaper>
      </Container>
    );
  }
  
  // Cart with items
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <IconButton onClick={goToShop} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" display="inline">
          Your Cart
        </Typography>
      </Box>
      
      <Grid container spacing={4}>
        {/* Cart Items */}
        <Grid item xs={12} md={8}>
          <StyledPaper>
            <Typography variant="h5" gutterBottom>Cart Items</Typography>
            
            <TableContainer component={Box} sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="center">Price</TableCell>
                    <TableCell align="center">Quantity</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cartItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {item.image && (
                            <Box 
                              component="img" 
                              src={item.image.startsWith('http') ? item.image : '/qr-placeholder.png'} 
                              alt={item.name}
                              sx={{ width: 60, height: 60, mr: 2, borderRadius: 1 }}
                            />
                          )}
                          <Box>
                            <Typography variant="subtitle1">{item.name}</Typography>
                            <Typography variant="body2" color="text.secondary">{item.description}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">{formatCurrency(item.price)}</TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          InputProps={{ inputProps: { min: 1, max: 99 } }}
                          value={item.quantity}
                          onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                          sx={{ width: 70 }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(item.price * item.quantity)}</TableCell>
                      <TableCell align="right">
                        <IconButton 
                          color="error" 
                          size="small"
                          onClick={() => removeFromCart(item.id)}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button 
                variant="outlined" 
                startIcon={<ShoppingCartIcon />}
                onClick={goToShop}
              >
                Continue Shopping
              </Button>
            </Box>
          </StyledPaper>
        </Grid>
        
        {/* Order Summary */}
        <Grid item xs={12} md={4}>
          <StyledPaper>
            <Typography variant="h5" gutterBottom>Order Summary</Typography>
            
            <Box sx={{ mt: 3, mb: 4 }}>
              <SummaryItem>
                <Typography variant="body1">Subtotal</Typography>
                <Typography variant="body1">{formatCurrency(calculateSubtotal())}</Typography>
              </SummaryItem>
              
              <SummaryItem>
                <Typography variant="body1">Shipping</Typography>
                <Typography variant="body1">
                  {calculateShipping() === 0 
                    ? 'Free' 
                    : formatCurrency(calculateShipping())}
                </Typography>
              </SummaryItem>
              
              <SummaryItem>
                <Typography variant="body1">Tax</Typography>
                <Typography variant="body1">{formatCurrency(calculateTax())}</Typography>
              </SummaryItem>
              
              <Divider sx={{ my: 2 }} />
              
              <SummaryItem>
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6">{formatCurrency(calculateTotal())}</Typography>
              </SummaryItem>
            </Box>
            
            <Button 
              variant="contained" 
              color="primary"
              size="large"
              fullWidth
              startIcon={<PaymentIcon />}
              onClick={() => navigate('/checkout')}
              sx={{ py: 1.5 }}
            >
              Continue to Checkout
            </Button>
            
            <Box sx={{ mt: 3 }}>
              <Alert severity="info" sx={{ fontSize: '0.9rem' }}>
                By proceeding to checkout, you're confirming that your order details are correct.
              </Alert>
            </Box>
          </StyledPaper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Cart;